import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * LAP — Deep-link entity binding (surlignage).
 *
 * Les cartes du fil d'actualité pilotées par une entité (objectif, tâche,
 * document…) pointent désormais vers `<route>?focus=<id>`. Ce hook lit ce
 * paramètre et permet à une page liste de SURLIGNER puis d'oublier la ligne
 * ciblée, pour que l'utilisateur voie immédiatement DE QUELLE entité parlait
 * la carte.
 *
 * @param {string} paramName  nom du query param (défaut "focus")
 * @param {number} autoClearMs  délai avant d'estomper le surlignage (défaut 6000ms ; 0 = jamais)
 * @returns {{ focusId: string|null, isFocused: (id) => boolean, clear: () => void }}
 */
export function useFocusHighlight(paramName = 'focus', autoClearMs = 6000) {
  const location = useLocation()

  const rawFocus = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const value = params.get(paramName)
    return value != null && value !== '' ? value : null
  }, [location.search, paramName])

  const [focusId, setFocusId] = useState(rawFocus)

  useEffect(() => {
    setFocusId(rawFocus)
  }, [rawFocus])

  useEffect(() => {
    if (!focusId || !autoClearMs) return undefined
    const timer = setTimeout(() => setFocusId(null), autoClearMs)
    return () => clearTimeout(timer)
  }, [focusId, autoClearMs])

  // Comparaison tolérante au type (les ids d'URL sont des strings, les ids de
  // ligne peuvent être des nombres).
  const isFocused = (id) => focusId != null && String(id) === String(focusId)

  return { focusId, isFocused, clear: () => setFocusId(null) }
}

/**
 * Style MUI réutilisable pour la ligne surlignée d'une DataGrid.
 * À injecter dans la prop `sx` de la DataGrid, la classe étant appliquée via
 * `getRowClassName={(p) => isFocused(p.id) ? FOCUS_ROW_CLASS : ''}`.
 */
export const FOCUS_ROW_CLASS = 'lap-focus-row'

export const focusRowSx = {
  [`& .${FOCUS_ROW_CLASS}`]: {
    backgroundColor: 'rgba(139, 92, 246, 0.18)',
    boxShadow: 'inset 3px 0 0 0 #8b5cf6',
    animation: 'lapFocusPulse 1.4s ease-in-out 2',
  },
  '@keyframes lapFocusPulse': {
    '0%': { backgroundColor: 'rgba(139, 92, 246, 0.30)' },
    '50%': { backgroundColor: 'rgba(139, 92, 246, 0.10)' },
    '100%': { backgroundColor: 'rgba(139, 92, 246, 0.18)' },
  },
}

export default useFocusHighlight
